import { Head, Link } from '@inertiajs/react'
import { Server, AppWindow, CheckCircle, AlertCircle, HardDrive, Clock } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { StatusBadge } from '@/components/status-badge'
import AppLayout from '@/layouts/app-layout'
import { dashboardPath, serverPath, appPath, appBackupPath } from '@/routes'
import type { BreadcrumbItem, Server as ServerType, App, Backup, BackupConfig, HealthCheck } from '@/types'

type DashboardProps = {
  servers: (ServerType & { apps: Pick<App, 'id' | 'name'>[] })[]
  recent_backups: (Backup & { 
    app: Pick<App, 'id' | 'name'>
    backup_config: Pick<BackupConfig, 'backup_type'>
  })[]
  recent_health_checks: (HealthCheck & {
    app?: Pick<App, 'id' | 'name'>
    server?: Pick<ServerType, 'id' | 'name'>
  })[]
  disk_usage: {
    server_id: number
    server_name: string
    usage_percent?: number
    used_bytes?: number
    total_bytes?: number
    breakdown: Record<string, number>
  }[]
  stats: {
    total_servers: number
    total_apps: number
    successful_backups_today: number
    failed_backups_today: number
    health_issues: number
  }
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: 'Dashboard', href: dashboardPath() },
]

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return 'N/A'
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes === 0) return '0 Bytes'
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatDate(date: string | null | undefined): string {
  if (!date) return 'N/A'
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function Dashboard({ servers, recent_backups, recent_health_checks, disk_usage, stats }: DashboardProps) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Dashboard" />

      <div className="p-6">
        <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Servers</CardTitle>
              <Server className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_servers}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Applications</CardTitle>
              <AppWindow className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_apps}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Backups Today</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.successful_backups_today}</div>
              {stats.failed_backups_today > 0 && (
                <p className="text-xs text-destructive">
                  {stats.failed_backups_today} failed
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Health Issues</CardTitle>
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${stats.health_issues > 0 ? 'text-destructive' : ''}`}>
                {stats.health_issues}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Disk Usage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />
                Disk Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              {disk_usage.length === 0 ? (
                <p className="text-sm text-muted-foreground">No disk usage data available</p>
              ) : (
                <div className="space-y-4">
                  {disk_usage.map((usage) => (
                    <div key={usage.server_id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Link 
                          href={serverPath(usage.server_id)}
                          className="font-medium hover:underline"
                        >
                          {usage.server_name}
                        </Link>
                        <span className={`text-sm ${
                          (usage.usage_percent || 0) > 90 ? 'text-destructive' :
                          (usage.usage_percent || 0) > 80 ? 'text-yellow-500' : ''
                        }`}>
                          {usage.usage_percent?.toFixed(1) || 0}%
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-muted">
                        <div 
                          className={`h-2 rounded-full ${
                            (usage.usage_percent || 0) > 90 ? 'bg-destructive' :
                            (usage.usage_percent || 0) > 80 ? 'bg-yellow-500' : 'bg-primary'
                          }`}
                          style={{ width: `${Math.min(usage.usage_percent || 0, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatBytes(usage.used_bytes)} of {formatBytes(usage.total_bytes)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Backups */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Recent Backups
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recent_backups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent backups</p>
              ) : (
                <div className="space-y-3">
                  {recent_backups.slice(0, 5).map((backup) => (
                    <div key={backup.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link 
                          href={appBackupPath(backup.app.id, backup.id)}
                          className="font-medium hover:underline"
                        >
                          {backup.app.name}
                        </Link>
                        <Badge variant="outline" className="text-xs">
                          {backup.backup_config.backup_type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {formatDate(backup.completed_at)}
                        </span>
                        <StatusBadge 
                          status={backup.status as 'success' | 'error' | 'pending' | 'running'}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Servers List */}
        <Card>
          <CardHeader>
            <CardTitle>Servers & Applications</CardTitle>
            <CardDescription>Quick overview of all servers and their apps</CardDescription>
          </CardHeader>
          <CardContent>
            {servers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No servers configured</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {servers.map((server) => (
                  <Link key={server.id} href={serverPath(server.id)}>
                    <div className="rounded-lg border p-4 transition-colors hover:bg-muted/50">
                      <div className="flex items-center gap-2 font-medium">
                        <Server className="h-4 w-4" />
                        {server.name}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {server.apps.map((app) => (
                          <Badge key={app.id} variant="secondary" className="text-xs">
                            {app.name}
                          </Badge>
                        ))}
                        {server.apps.length === 0 && (
                          <span className="text-xs text-muted-foreground">No apps</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>
    </AppLayout>
  )
}
